'use client';

import { JSX, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import {
    ImageFileInput,
    ImageFileInputDefaultState,
    ImageFileInputLoadingState,
    ImageFileInputProps,
    ImageFileInputSuccessState,
    Label,
    Text,
    useImageFileInput,
    type FileInputErrorDetails,
} from '@videoblocks/design-system';

import { AI_TOOLKIT_CATEGORIES, AiToolkitCategory } from '../../_context/types';

export type LibraryImagePreview = {
    thumbnailUrl: string;
    fileName: string;
};

export interface FileUploadInputFieldProps extends ImageFileInputProps {
    fileInputClassName?: string;
    acceptOptions: string[];
    toolkitCategory: Extract<AiToolkitCategory, 'video-generation' | 'image-generation'>;
    label?: string;
    maxSizeBytes?: number;
    maxFiles?: number;
    error?: string | null;
    value?: File | File[] | null;
    libraryImage?: LibraryImagePreview | null;
    onFileSelect?: (file: File | File[]) => void;
    onFileRemove?: () => void;
    onFileRemoveAtIndex?: (index: number) => void;
    onFileError?: (details: FileInputErrorDetails) => string | void;
    onBrowseLibrary?: () => void;
}

export const FileUploadInputField = ({
    acceptOptions,
    fileInputClassName,
    maxSizeBytes,
    maxFiles,
    toolkitCategory,
    label,
    className,
    error,
    value,
    multiple,
    libraryImage,
    onFileSelect,
    onFileRemove,
    onFileRemoveAtIndex,
    onFileError,
    onBrowseLibrary,
    disabled,
    ...rest
}: FileUploadInputFieldProps): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {
        state,
        file,
        previewUrls,
        handleChange,
        error: fileInputError,
        handleDrop,
        openFilePicker,
        reset,
    } = useImageFileInput(fileInputRef, {
        accept: acceptOptions,
        maxSizeBytes,
        multiple,
        maxFiles,
        error,
        value,
        onError: onFileError,
        onFileSelected: onFileSelect,
    });

    useEffect(() => {
        if (libraryImage) {
            // Reset the file input hook's internal state when a library image is selected.
            reset();
        } else if (fileInputRef.current) {
            // Clear the DOM value so the same file can be re-selected after a library image is removed.
            fileInputRef.current.value = '';
        }
    }, [libraryImage]);

    const videoGenerationDescription = (
        <div className="flex flex-col text-center">
            <Text fontSize="xs" fontWeight="regular">
                <span
                    className={twMerge('underline cursor-pointer', disabled && 'cursor-not-allowed')}
                    onClick={() => {
                        if (disabled) return;
                        openFilePicker();
                    }}
                >
                    Upload
                </span>{' '}
                or{' '}
                <span
                    className={twMerge('underline cursor-pointer', disabled && 'cursor-not-allowed')}
                    onClick={() => {
                        if (disabled) return;
                        onBrowseLibrary?.();
                    }}
                >
                    browse
                </span>{' '}
                for a start frame
            </Text>
            <Text fontSize="xs" fontWeight="regular">
                JPEG, PNG, or WebP · Max 15MB
            </Text>
        </div>
    );

    const imageGenerationDescription = (
        <div className="flex flex-col text-center">
            <Text fontSize="xs" fontWeight="regular">
                <span
                    className={twMerge('underline cursor-pointer', disabled && 'cursor-not-allowed')}
                    onClick={() => {
                        if (disabled) return;
                        openFilePicker();
                    }}
                >
                    Upload
                </span>{' '}
                up to 3 images
            </Text>
            <Text fontSize="xs" fontWeight="regular">
                JPEG, PNG, or WebP · Max 15MB
            </Text>
        </div>
    );

    const description =
        toolkitCategory === AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION
            ? videoGenerationDescription
            : toolkitCategory === AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION
              ? imageGenerationDescription
              : null;
    const singleFile = Array.isArray(file) ? file[0] : file;
    const displayPreviewUrl = libraryImage?.thumbnailUrl ?? previewUrls?.[0] ?? '';
    const displayFileName = libraryImage?.fileName ?? singleFile?.name ?? '';

    // A library image shows as success unless there is an active file-input error, which takes precedence.
    const displayState = libraryImage && state !== 'error' ? 'success' : state;

    const handleImageRemoveAtIndex = (index: number): void => {
        // Clear the hook's internal error state when removing an image.
        reset();
        onFileRemoveAtIndex?.(index);
    };

    const defaultState = (
        <ImageFileInputDefaultState
            {...(multiple ? { multiple, previewUrls: previewUrls ?? [], onImageClose: handleImageRemoveAtIndex } : {})}
        >
            {description}
        </ImageFileInputDefaultState>
    );

    return (
        <div className={twMerge('flex flex-col gap-2', className)}>
            <Label htmlFor="file-upload" className="text-left" fontSize="sm">
                {label}
            </Label>
            <ImageFileInput
                ref={fileInputRef}
                state={displayState}
                multiple={multiple}
                onChange={(e) => void handleChange(e)}
                onFileDrop={(files) => void handleDrop(files)}
                helperText={fileInputError ?? undefined}
                accept={acceptOptions.join(', ')}
                useButtonWrapper={false}
                id="file-upload"
                name="file-upload"
                className={fileInputClassName}
                disabled={disabled}
                {...rest}
            >
                {displayState === 'default' && defaultState}
                {displayState === 'loading' && <ImageFileInputLoadingState />}
                {displayState === 'success' &&
                    (multiple ? (
                        defaultState
                    ) : (
                        <ImageFileInputSuccessState
                            disabled={disabled}
                            previewUrl={displayPreviewUrl}
                            fileName={displayFileName}
                            onRemove={() => {
                                if (disabled) return;
                                reset();
                                onFileRemove?.();
                            }}
                        />
                    ))}
                {displayState === 'error' && defaultState}
            </ImageFileInput>
        </div>
    );
};
